'use client';
import { createContext, type ReactNode, useMemo } from 'react';
import type { FeatureFlags } from 'api/getFeatureFlags';
import type { AdSizePredictionProps } from 'types/ads';
import type { StructureTypes } from 'types/cms';
import type { TickarooPrefetchData } from 'types/storylines';
import type { EmbedTypes } from 'types/tracking';
import { initErrorReporting } from 'utils/error';

export enum PageType {
  Overview,
  Article,
}

export interface AppContextState {
  pageType: StructureTypes;
  pageTitle?: string;
  adSizePrediction?: AdSizePredictionProps;
  adDebugMode?: boolean;
  analyticsData: AnalyticsDataProps;
  pageMetaData: PageMetaDataProps;
  prefetchedData?: TickarooPrefetchData;
  pushedAdSlots: Array<string>;
  requestPath?: string;
  featureFlags?: FeatureFlags;
  env?: string;
  version?: string;
  device?: string;
  os?: string;
  trafficSource?: string;
  sectionLogoColors?: [string, string, string];
}

interface PageMetaDataProps {
  adZone: string;
  noAds: boolean;
  isStoryline: boolean;
  noVideoAds?: boolean;
  overrideAdZone?: string;
  keywords?: Array<string>;
  adTags?: Array<string>;
}

export interface AnalyticsDataArticleProps {
  id?: string;
  type?: string;
  first_headline?: string;
  headline?: string;
  embeds?: Array<EmbedTypes>;
  tags?: string | Array<string>;
  first_publication_datetime?: string;
  last_update_datetime?: string;
  editorial_unit?: string;
  current_picture?: number;
  current_picture_id?: string;
  current_picture_url?: string;
  number_of_pictures?: number;
  word_count?: number;
}

export interface AnalyticsDataProps {
  ivwCode: string;
  homeSectionPath?: string;
}

export const initialAppContext = {
  pageType: 'page' as StructureTypes,
  pageTitle: undefined,
  adSizePrediction: undefined,
  adDebugMode: process.env.NEXT_PUBLIC_ENABLE_AD_DEBUG_MODE == 'true',

  analyticsData: {
    ivwCode: 'rtl_portal/news',
    homeSectionPath: undefined,
    articleFields: undefined,
  },
  pageMetaData: {
    adZone: '_default',
    keywords: [],
    adTags: [],
    noAds: false,
    noVideoAds: false,
    isStoryline: false,
  },
  prefetchedData: undefined,
  pushedAdSlots: [],
  device: 'mobile',
  os: 'android',
  trafficSource: 'direct',
  sectionLogoColors: undefined,
};

interface AppProviderProps {
  children: ReactNode;
  value: Partial<AppContextState>;
}

export const AppContext = createContext<AppContextState>(initialAppContext);

export const AppProvider = ({ children, value }: AppProviderProps) => {
  if (globalThis.window !== undefined) {
    initErrorReporting();
  }

  const state = useMemo(() => {
    return {
      ...initialAppContext,
      ...value,
      pageMetaData: {
        ...initialAppContext.pageMetaData,
        ...value.pageMetaData,
      },
      analyticsData: {
        ...initialAppContext.analyticsData,
        ...value.analyticsData,
      },
    };
  }, [value]);

  return <AppContext.Provider value={state}>{children}</AppContext.Provider>;
};
